import Link from "next/link";
import Image from "next/image";
import { Brain, type LucideIcon } from "lucide-react";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "~/components/ui/item";
import { Badge } from "~/components/ui/badge";
import type { QuizConfig } from "~/types/quiz";
import type { QuizCategoryLabel } from "~/config/quiz-categories";

interface SerializedQuizConfig {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  longDescription?: string;
  duration: string;
  icon: LucideIcon;
  illustration?: QuizConfig["illustration"];
  completions: number;
  category: QuizCategoryLabel;
  isFeatured?: boolean;
  questions: unknown[];
  copy: unknown;
}

interface QuizCardProps {
  quiz: SerializedQuizConfig;
}

export function QuizCard({ quiz }: QuizCardProps) {
  const IconComponent = quiz.icon ?? Brain;

  const complitions = (quiz.completions ?? 0) * 5;

  return (
    <Item
      asChild
      className="group focus-visible:outline-primary relative flex h-full flex-col items-stretch rounded-none border-0 bg-transparent p-0 hover:!bg-transparent focus-visible:outline-2 focus-visible:outline-offset-4"
    >
      <Link
        href={`/testovi/${quiz.id}`}
        aria-label={`Otvori test ${quiz.title}`}
      >
        <div className="flex h-full flex-col">
          <div className="bg-muted/40 relative aspect-[3/2] w-full overflow-hidden rounded-lg">
            {quiz.illustration ? (
              <Image
                src={quiz.illustration.src}
                alt={quiz.illustration.alt}
                fill
                sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
                className="object-cover"
              />
            ) : (
              <>
                <div className="from-brand/20 absolute inset-0 bg-linear-to-br via-transparent to-transparent opacity-0 transition duration-300 group-hover:opacity-100" />
                <div className="relative flex h-full items-center justify-center">
                  <IconComponent className="text-muted-foreground group-hover:text-foreground h-10 w-10 transition-colors duration-300" />
                </div>
              </>
            )}
          </div>

          <ItemContent className="flex-1 gap-3 pt-4">
            <ItemTitle className="text-card-foreground text-lg font-semibold underline-offset-4 group-hover:underline md:text-xl">
              {quiz.title}
            </ItemTitle>
            <ItemDescription className="line-clamp-2 text-sm">
              {quiz.longDescription ?? quiz.description}
            </ItemDescription>
            <div className="text-muted-foreground mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-xs font-medium">
              {complitions < 100 ? (
                <>
                  <Badge variant="success">Novi test</Badge>
                  <span aria-hidden="true">·</span>
                  <span>{quiz.duration}</span>
                  {quiz.category ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{quiz.category}</span>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <span>{quiz.duration}</span>
                  <span aria-hidden="true">·</span>
                  <span>{complitions.toLocaleString("sr-RS")} ljudi</span>
                  {quiz.category ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{quiz.category}</span>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </ItemContent>
        </div>
      </Link>
    </Item>
  );
}
